import * as React from 'react';

import { RowTitle } from 'common/components';
import { useModalContext } from 'common/contexts';
import usePluggableEntityTableElements from 'hooks/usePluggableEntityTableElements';
import StepActionsButton from 'security-app/components/SecurityEvents/EventProcedures/plugin/StepList/StepActionsButton';
import type { ColumnRenderers } from 'components/common/EntityDataTable';
import type { EventProcedureStepAPIType } from 'security-app/hooks/api/eventProceduresAPI.types';

import DescriptionCell from '../DescriptionCell';

const TitleCell = ({ eventProcedureStep }: { eventProcedureStep: EventProcedureStepAPIType }) => {
  const [selectedStepId, setSelectedStepId] = React.useState(undefined);
  const { setModal, setEntity } = useModalContext();

  const onClickStep = () => {
    setSelectedStepId(eventProcedureStep.id);
    setEntity(eventProcedureStep);
    setModal('EDIT-EVENT-PROCEDURE-STEP');
  };

  React.useEffect(() => {
    if (eventProcedureStep.id === selectedStepId) setEntity(eventProcedureStep);
  }, [eventProcedureStep, selectedStepId, setEntity]);

  return <RowTitle onClick={onClickStep}>{eventProcedureStep?.title || 'No title set'}</RowTitle>;
};

// eslint-disable-next-line import/prefer-default-export
export const useColumnRenderers = () => {
  const { pluggableColumnRenderers }: any = usePluggableEntityTableElements<EventProcedureStepAPIType>(
    null,
    'event_procedure_step',
  );

  const collectionsRenderer = React.useMemo(
    () =>
      pluggableColumnRenderers?.collections
        ? pluggableColumnRenderers
        : {
            collections: {
              staticWidth: 130,
              renderCell: () => <i>Unauthorized</i>,
            },
          },
    [pluggableColumnRenderers],
  );

  const columnRenderers: ColumnRenderers<EventProcedureStepAPIType> = React.useMemo(
    () => ({
      attributes: {
        title: {
          width: 0.3,
          renderCell: (_: string, eventProcedureStep: EventProcedureStepAPIType) => (
            <TitleCell eventProcedureStep={eventProcedureStep} />
          ),
        },
        description: {
          width: 0.7,
          renderCell: (_: string, step: EventProcedureStepAPIType) => <DescriptionCell mdValue={step.description} />,
        },
        type: {
          renderCell: (_: string, step: EventProcedureStepAPIType) => <StepActionsButton step={step} />,
          staticWidth: 130,
        },
        ...collectionsRenderer,
      },
    }),
    [collectionsRenderer],
  );

  return { columnRenderers };
};
